import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import PublicForm from "@/pages/PublicForm";
import QuickLead from "@/pages/QuickLead";
import Login from "@/pages/Login";
import TrackQuote from "@/pages/TrackQuote";
import BidPortal from "@/pages/BidPortal";
import VendorDocsPage from "@/pages/VendorDocsPage";
import DashboardLayout from "@/components/DashboardLayout";
import DashboardHome from "@/pages/DashboardHome";
import LeadsPage from "@/pages/LeadsPage";
import UsersPage from "@/pages/UsersPage";
import VendorsPage from "@/pages/VendorsPage";
import AccountsPage from "@/pages/AccountsPage";
import WhatsAppBid from "@/pages/WhatsAppBid";
import FeedbackPage from "@/pages/FeedbackPage";
import DriverTrack from "@/pages/DriverTrack";
import TrackingPage from "@/pages/TrackingPage";
import ConsignmentTrack from "@/pages/ConsignmentTrack";
import PartnerFeedbackPage from "@/pages/PartnerFeedbackPage";
import VendorQuotesPage from "@/pages/VendorQuotesPage";
import PackingSchedule from "@/pages/UpcomingOrders";
import ILOTPage from "@/pages/ILOTPage";
import VendorPaymentsPage from "@/pages/VendorPaymentsPage";
import VehiclesPage from "@/pages/VehiclesPage";
import OfficeManagementPage from "@/pages/OfficeManagementPage";
import HistoryPage from "@/pages/HistoryPage";
import CustomersPage from "@/pages/CustomersPage";
import InsurancePage from "@/pages/InsurancePage";
import InvoicePage from "@/pages/InvoicePage";
import MetaLeadsPage from "@/pages/MetaLeadsPage";
import PublicInvoice from "@/pages/PublicInvoice";

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  useEffect(() => {
    if (localStorage.getItem("arrow_theme") !== "light") document.documentElement.classList.add("dark");
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicForm />} />
            <Route path="/quick-lead" element={<QuickLead />} />
            <Route path="/login" element={<Login />} />
            <Route path="/track/:token" element={<TrackQuote />} />
            <Route path="/track/:token/invoice/:invoiceId" element={<PublicInvoice />} />
            <Route path="/track-shipment" element={<ConsignmentTrack />} />
            <Route path="/bid/:token" element={<BidPortal />} />
            <Route path="/vendor-docs/:token" element={<VendorDocsPage />} />
            <Route path="/wq/:token" element={<WhatsAppBid />} />
            <Route path="/feedback/:token" element={<FeedbackPage />} />
            <Route path="/drive/:token" element={<DriverTrack />} />
            <Route
              path="/dashboard"
              element={
                <Protected>
                  <DashboardLayout />
                </Protected>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route
                path="meta-leads"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <MetaLeadsPage />
                  </Protected>
                }
              />
              <Route
                path="invoices/:invoiceId"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <InvoicePage />
                  </Protected>
                }
              />
              <Route
                path="invoice/:leadId"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <InvoicePage />
                  </Protected>
                }
              />
              <Route path="ilot" element={<ILOTPage />} />
              <Route path="upcoming" element={<PackingSchedule />} />
              <Route
                path="users"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <UsersPage />
                  </Protected>
                }
              />
              <Route
                path="customers"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <CustomersPage />
                  </Protected>
                }
              />
              <Route
                path="accounts"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <AccountsPage />
                  </Protected>
                }
              />
              <Route
                path="vendor-payments"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <VendorPaymentsPage />
                  </Protected>
                }
              />
              <Route
                path="vendors"
                element={
                  <Protected roles={["admin", "manager", "calling_executive"]}>
                    <VendorsPage />
                  </Protected>
                }
              />
              <Route
                path="vehicles"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <VehiclesPage />
                  </Protected>
                }
              />
              <Route
                path="office"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <OfficeManagementPage />
                  </Protected>
                }
              />
              <Route
                path="history"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <HistoryPage />
                  </Protected>
                }
              />
              <Route
                path="tracking"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <TrackingPage />
                  </Protected>
                }
              />
              <Route
                path="quotes"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <VendorQuotesPage />
                  </Protected>
                }
              />
              <Route
                path="insurance"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <InsurancePage />
                  </Protected>
                }
              />
              <Route
                path="feedback"
                element={
                  <Protected roles={["admin", "manager"]}>
                    <PartnerFeedbackPage />
                  </Protected>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
